import React from "react";
import { HeaderBox, Pad, PadBox, WindowTitle } from "component/basics";
import { ConversationScreen } from "component/screen";
import { ActionEdit, ActionReply, BasicComments, CommentConfig, CommentPageTopWidgets, CommentsInput, ComposerScreen } from "component/comment";
import { Heading, UtilityText } from "component/text";
import { useConfig } from "util/features";
import { Banner } from "component/banner";
import { TeaserScreen } from "system/teaser";
import { Catcher } from "system/catcher";
import { StandardConfig } from "util/stdconfig";
import { Structure } from "util/stdtypes";

export type SimpleCommentsConfig = CommentConfig & StandardConfig & {
    composerPreview: React.ComponentType<{comment: Comment}>;
    headerTopLeftWidgets: React.ComponentType<{}>[];
    topBanners: React.ComponentType<{}>[];
    teaserScreen: React.ComponentType<{}> | null;
};

export const SimpleCommentsStructure : Structure<SimpleCommentsConfig> = {
    key: 'simplecomments',
    name: 'Simple Comments',
    screen: SimpleCommentsScreen,
    teaser: CommentTeaserScreen,
    subscreens: {
        composer: ({about, commentKey}) => <ComposerScreen about={about} commentKey={commentKey} intro={null} />,
    },
    defaultConfig: {
        commentActions: [ActionReply],
        commentRightActions: [ActionEdit],
        commentTopWidgets: [],
        commentBodyRenderer: null,
        commentBodyStylers: [],
        commentMiddleWidgets: [],
        commentAboveWidgets: [],
        commentBelowWidgets: [],
        commentAllowEmpty: false,
        commentDeleteTriggers: [],
        commentLimit: null,
        commentEditBottomWidgets: [],
        commentEditTopWidgets: [],
        commentPostBlockers: [],
        commentPostActionLabel: null,
        commentPostCheckers: [],
        commentPostTriggers: [],
        commentStylers: [],
        commentInputPlaceholder: 'Share your thoughts...',
        commentReplyPlaceholder: 'Reply to {authorName}...',
        commentInputLoginAction: '',
        commentFilters: [],
        commentRankers: [],

        headerTopLeftWidgets: [],

        replyAboveWidgets: [],
        replyFilters: [],
        replyBooster: undefined,
        replyNotifsDisabled: false,

        composerSubtitle: () => 'Public Comment',
        composerTopBanners: [],
        composerTopWidgets: [],
        composerPreview: CommentsInput,

        topBanners: [],
        pageBottomWidgets: [],
        pageTopWidgets: [],
        pageTopRightWidgets: [],
        pageTopLeftWidgets: [],        
        pageShowEmptyHelp: true,
        pageHiddenWidgets: [],
        
        teaserScreen: null,
        noCommentsTitle: 'Start the conversation',
        noCommentsMessage: 'Be the first to share your thoughts',
        noMoreCommentsMessage: 'No more responses',
        readOnly: false,
        
        topBarHelpBubbles: [],
        openLinksInNewTab: false,
    }
}

function HeaderTopWidgets() {
    const {headerTopLeftWidgets} = useConfig<SimpleCommentsConfig>();
    if (headerTopLeftWidgets?.length > 0) {
        return <>
            <Pad size={12}/>
            {headerTopLeftWidgets.map((Widget,i) => <Widget key={i}/>)}
            <Pad size={32}/>
        </>
    } else {
        return null;
    }
}

export function SimpleCommentsScreen() {
    const {topBanners, composerPreview} = useConfig<SimpleCommentsConfig>();
    return <ConversationScreen>
        <WindowTitle label='Comments' />
        {topBanners?.map((Banner, i) => <Banner key={i} />)}
        <Catcher>
            <HeaderBox>
                <HeaderTopWidgets />
                <Heading type="large" weight="medium" label='Comments'/>
                <Pad />
                {React.createElement(composerPreview)}
            </HeaderBox>
        </Catcher>
        <Catcher>
            <CommentPageTopWidgets />
            <PadBox horiz={20} top={32}>
                <BasicComments showInput={false} />
            </PadBox>
        </Catcher>
    </ConversationScreen>
}

function CommentTeaserScreen() {
    const {teaserScreen} = useConfig<SimpleCommentsConfig>();
    if (teaserScreen) {
        return React.createElement(teaserScreen);
    } else {
        return <TeaserScreen><Banner><UtilityText label='No teaser screen configured' /></Banner></TeaserScreen>
    }
}

